import React, { useState, useMemo } from 'react';
import { ListTodo, Filter, ChevronDown, ChevronUp, ArrowUpDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import TaskCard, { EFFORT_OPTIONS } from './TaskCard';
import EffortDisplay from './EffortDisplay';
import { OrkaPane } from '../orka';

const SLOT_HEIGHT = 30;

const ScheduledPane = ({
  tasks,
  borderColor,
  totalEffort,
  taskCount,
  onTaskUpdate,
  onTaskDelete,
  onMoveToBacklog,
  draggingTask,
  onDragStart,
  onDragEnd,
  onDropFromTimeline
}) => {
  const [isOver, setIsOver] = useState(false);
  const [showFilterSort, setShowFilterSort] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterWorkArea, setFilterWorkArea] = useState('all');
  const [sortBy, setSortBy] = useState('task_priority');
  const [sortDir, setSortDir] = useState('asc');

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = () => {
    setIsOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsOver(false);
    if (draggingTask && onDropFromTimeline) {
      onDropFromTimeline(draggingTask);
    }
    onDragEnd();
  };

  // Get unique work areas
  const workAreas = useMemo(() => {
    const areas = new Set(tasks.map(t => t.work_area_id || 'General'));
    return Array.from(areas).sort();
  }, [tasks]);

  // Filter and sort tasks
  const filteredTasks = useMemo(() => {
    let result = tasks;
    
    // Text filter
    if (filterText) {
      const query = filterText.toLowerCase();
      result = result.filter(t => t.task_title.toLowerCase().includes(query));
    }
    
    // Priority filter
    if (filterPriority !== 'all') {
      result = result.filter(t => String(t.task_priority ?? 3) === filterPriority);
    }
    
    // Work area filter
    if (filterWorkArea !== 'all') {
      result = result.filter(t => (t.work_area_id || 'General') === filterWorkArea);
    }
    
    // Sort
    result = [...result].sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'task_priority') {
        aVal = a.task_priority ?? 3;
        bVal = b.task_priority ?? 3;
      } else if (sortBy === 'task_estimated_effort') {
        aVal = a.task_estimated_effort || 1;
        bVal = b.task_estimated_effort || 1;
      } else if (sortBy === 'title') {
        aVal = a.title.toLowerCase();
        bVal = b.title.toLowerCase();
      } else {
        aVal = a.created_at || '';
        bVal = b.created_at || '';
      }
      
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [tasks, filterText, filterPriority, filterWorkArea, sortBy, sortDir]);

  const hasFilters = filterText || filterPriority !== 'all' || filterWorkArea !== 'all';

  // Calculate duration based on actual times or effort
  const estimatedMinutes = filteredTasks.reduce((sum, t) => {
    if (t.task_local_start_time && t.task_local_end_time) {
      const [sh, sm] = t.task_local_start_time.split(':').map(Number);
      const [eh, em] = t.task_local_end_time.split(':').map(Number);
      return sum + ((eh * 60 + em) - (sh * 60 + sm));
    }
    const effortOpt = EFFORT_OPTIONS.find(e => e.value === t.task_estimated_effort);
    return sum + (effortOpt?.minutes || 30);
  }, 0);
  const hours = Math.floor(estimatedMinutes / 60);
  const mins = estimatedMinutes % 60;

  // Calculate card height based on actual duration or effort-to-time conversion
  const getCardHeight = (task) => {
    let durationMinutes;
    
    if (task.task_local_start_time && task.task_local_end_time) {
      const [sh, sm] = task.task_local_start_time.split(':').map(Number);
      const [eh, em] = task.task_local_end_time.split(':').map(Number);
      durationMinutes = (eh * 60 + em) - (sh * 60 + sm);
    } else {
      const effortOpt = EFFORT_OPTIONS.find(e => e.value === task.task_estimated_effort);
      durationMinutes = effortOpt?.minutes || 30;
    }
    
    const slots = Math.ceil(durationMinutes / 15);
    return Math.max(slots * SLOT_HEIGHT, SLOT_HEIGHT * 2);
  };

  return (
    <OrkaPane
      title="Today - Unplanned"
      intent="plan"
      icon={ListTodo}
      borderTone={borderColor}
      stats={{
        effort: totalEffort,
        duration: `~${hours}h ${mins}m`,
        filtered: filteredTasks.length,
        total: taskCount,
      }}
      filterActive={!!hasFilters}
      rightSlot={
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => setShowFilterSort(!showFilterSort)}
          data-testid="scheduled-pane-filter-toggle"
        >
          <Filter className="w-3 h-3 mr-1" />
          {showFilterSort ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </Button>
      }
      filterSlot={
        showFilterSort ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="text"
                placeholder="Search..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="h-7 text-xs"
              />
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="-1">P-1</SelectItem>
                  <SelectItem value="0">P0</SelectItem>
                  <SelectItem value="1">P1</SelectItem>
                  <SelectItem value="2">P2</SelectItem>
                  <SelectItem value="3">P3</SelectItem>
                  <SelectItem value="4">P4</SelectItem>
                  <SelectItem value="5">P5</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={filterWorkArea} onValueChange={setFilterWorkArea}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Work Area" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Areas</SelectItem>
                  {workAreas.map(area => <SelectItem key={area} value={area}>{area}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex gap-1">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Sort by" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="task_priority">Priority</SelectItem>
                    <SelectItem value="task_estimated_effort">Effort</SelectItem>
                    <SelectItem value="title">Title</SelectItem>
                    <SelectItem value="created_at">Created</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}>
                  <ArrowUpDown className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        ) : null
      }
      isDropTarget={isOver}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      testId="scheduled-pane"
    >
      {filteredTasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full orka-text-tertiary">
          <ListTodo className="w-8 h-8 mb-2" aria-hidden="true" />
          <p className="text-sm">{hasFilters ? 'No matching tasks' : 'No unplanned tasks for today'}</p>
          <p className="text-xs mt-1">{hasFilters ? 'Try different filters' : 'Drag tasks here or create new ones'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTasks.map((task) => {
            const height = getCardHeight(task);
            return (
              <div key={task.task_id} style={{ height, overflow: 'visible' }}>
                <TaskCard
                  task={task}
                  context="unscheduled"
                  fixedHeight={height}
                  onUpdate={onTaskUpdate}
                  onDelete={onTaskDelete}
                  isDragging={draggingTask?.task_id === task.task_id}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                />
              </div>
            );
          })}
        </div>
      )}
    </OrkaPane>
  );
};

export default ScheduledPane;
